import Decimal from 'decimal.js';

import { Entity } from '../entity';
import { ActivityEntityName } from '../shared/activity.entity.name';
import { ActivityTagName } from '../shared/activity';
import { Invoice, InvoiceType } from './invoicing';
import { InvoiceStatus } from './invoicing.enum';
import { PaymentType, PaymentEditorInfo } from '../payment/payment';
import { BillCalculation } from './bill.calculation.domain';
import { IFinancialCalculation, InvoiceCalculationModel } from './financial.calculation';

/**
 * This class is make the invoice calculation easier to use and not repeat the same calculation in many places
 */
export class InvoiceCalculationDomain {

    static from(invoice: InvoiceCalculationModel, isBill: boolean): IFinancialCalculation {
        if (!invoice) {
            return null;
        }

        if (isBill) {
            if (!invoice.type) {
                invoice.type = this.getBillType(invoice.type)
            }

            return new BillCalculation(invoice);
        }

        if (!invoice.type) {
            invoice.type = this.getInvoiceType(invoice.type, invoice.total)
        }

        return new InvoiceCalculation(invoice);
    }

    static getInvoiceType(type: InvoiceType, total: number): InvoiceType {

        if (type === InvoiceType.PrepaymentInvoice) {
            return InvoiceType.PrepaymentInvoice;
        }

        if (total < 0 || type === InvoiceType.CreditNote) {
            return InvoiceType.CreditNote;
        }

        return InvoiceType.Invoice;
    }

    static getBillType(type: InvoiceType): InvoiceType {

        switch (type) {
            case InvoiceType.PrepaymentSupplier:
                return InvoiceType.PrepaymentSupplier;

            case InvoiceType.CreditNoteSupplier:
                return InvoiceType.CreditNoteSupplier;

            default:
                return InvoiceType.Bill
        }
    }
}

class InvoiceCalculation implements IFinancialCalculation {

    private readonly invoice: InvoiceCalculationModel;

    constructor(invoice: InvoiceCalculationModel) {

        let { prepaymentTotal, total, amountPaid, amountRefunded, creditAmount, roundingAmount, outstandingAmount, isReconcile } = invoice;

        this.invoice = invoice;

        this.invoice.amountPaid = amountPaid || 0;
        this.invoice.amountRefunded = amountRefunded || 0;
        this.invoice.roundingAmount = isReconcile ? (roundingAmount || 0) : 0;
        this.invoice.creditAmount = creditAmount || 0;
        this.invoice.total = total || 0;
        this.invoice.outstandingAmount = outstandingAmount || 0;
        this.invoice.prepaymentTotal = prepaymentTotal || 0;

    }

    public getInstance(): InvoiceCalculationModel {
        return this.invoice;
    }

    public get<K extends keyof InvoiceCalculationModel>(prop: K): InvoiceCalculationModel[K] {
        return this.invoice[prop];
    }

    public set<K extends keyof InvoiceCalculationModel>(
        prop: K,
        value: InvoiceCalculationModel[K]
    ): void {
        this.invoice[prop] = value;
    }

    public isFullyPaid(): boolean {

        const { type, prepaymentTotal, amountPaid } = this.invoice;

        switch (type) {
            case InvoiceType.CreditNote:
                return true;
            case InvoiceType.Invoice:
                return this.calculateOutstandingAmount() === 0;
            case InvoiceType.PrepaymentInvoice:
                return new Decimal(prepaymentTotal).minus(amountPaid).equals(0);
            default:
                return false;
        }

    }

    public isApproved(): boolean {
        return this.invoice.status === InvoiceStatus.Approved;
    }

    public isType(type: InvoiceType) {
        return this.invoice.type === type;
    }


    public getActivityEntityName(): ActivityEntityName {
        switch (this.invoice.type) {
            case InvoiceType.CreditNote:
                return ActivityEntityName.CreditNote;
            case InvoiceType.PrepaymentInvoice:
                return ActivityEntityName.PrepaymentInvoice;
            default:
                return ActivityEntityName.Invoice;
        }
    }

    public getActivityTagName(): ActivityTagName {
        switch (this.invoice.type) {
            case InvoiceType.CreditNote:
                return ActivityTagName.InvoiceId;
            case InvoiceType.PrepaymentInvoice:
                return ActivityTagName.PrepaymentInvoiceId;
            default:
                return ActivityTagName.InvoiceId;
        }
    }

    /** return updated invoice */
    public allocate(allocateAmount: number, currentUser?: Entity): Partial<Invoice> | null {

        allocateAmount = allocateAmount || 0;

        if (allocateAmount === 0) return null;

        const amount = this.calculateAmounts({ addCreditAmount: allocateAmount });

        const updatedInvoice: Partial<Invoice> = {
            _id: this.invoice._id,
            creditAmount: amount.creditAmount,
            outstandingAmount: amount.outstandingAmount,
            updatedBy: currentUser,
        }

        return updatedInvoice;
    }

    public canAllocate(): boolean {
        const { type } = this.invoice;

        if (!this.isApproved()) return false;

        switch (type) {
            case InvoiceType.CreditNote:
            case InvoiceType.PrepaymentInvoice:
                return this.getRemainingCredit() > 0;
            case InvoiceType.Invoice:
                return this.get('outstandingAmount') > 0;
            default:
                return false;
        }
    }

    public canMakePayment(): boolean {
        return this.getPaymentInfo() !== null;
    }

    public getPaymentInfo(): PaymentEditorInfo | null {

        if (!this.isApproved()) return null;

        const remainingCredit = this.calculateRemainingCredit();
        const refundableCreditNote = this.isType(InvoiceType.CreditNote) && remainingCredit > 0;
        const refundablePrepayment = this.isType(InvoiceType.PrepaymentInvoice) && remainingCredit > 0;

        if (refundableCreditNote) {
            return { amount: remainingCredit, amountEditable: true, title: 'Refund', type: PaymentType.ReceivableCredit };
        } else if (refundablePrepayment) {
            return { amount: remainingCredit, amountEditable: true, title: 'Refund', type: PaymentType.ReceivablePrepayment };
        }

        const outstandingAmount = this.calculateOutstandingAmount();
        const hasOutstandingAmountPrepayment = this.isType(InvoiceType.PrepaymentInvoice) && outstandingAmount > 0;
        const hasOutstandingAmountInvoice = this.isType(InvoiceType.Invoice) && outstandingAmount > 0;

        if (hasOutstandingAmountPrepayment || hasOutstandingAmountInvoice) {
            return { amount: outstandingAmount, amountEditable: hasOutstandingAmountInvoice, title: 'Receive', type: PaymentType.Receivable };
        }

        return null;
    }


    /**
     * This is the outstanding amount calculation. Not the remaining credit calculation 
     * @returns calculated outstanding amount
     */
    public calculateOutstandingAmount(): number {

        const { prepaymentTotal, total, amountPaid, creditAmount, roundingAmount, type } = this.invoice;

        switch (type) {
            case InvoiceType.CreditNote:
                return 0; // no payment for credit note
            case InvoiceType.PrepaymentInvoice:
                return new Decimal(prepaymentTotal - amountPaid).toDP(2).toNumber() // only if pay partially, but for now you only can pay 1 full payment
            default:
                // Invoice
                return new Decimal(total - (amountPaid + creditAmount + roundingAmount)).toDP(2).toNumber();
        }
    }

    public getRemainingCredit(): number {
        const { outstandingAmount, type } = this.invoice;
        switch (type) {
            case InvoiceType.CreditNote:
            case InvoiceType.PrepaymentInvoice:
                return Math.abs(outstandingAmount);
            default:
                return 0;
        }
    }

    public calculateRemainingCredit(): number {
        const { total, amountPaid, amountRefunded, prepaymentTotal, creditAmount, type, outstandingAmount } = this.invoice;
        switch (type) {
            case InvoiceType.CreditNote: {
                return Math.abs(total) - amountRefunded - creditAmount
            }
            case InvoiceType.PrepaymentInvoice: {

                const isNew = amountPaid === 0 && outstandingAmount === 0;
                if (isNew || !this.isFullyPaid()) {
                    return 0;
                }

                // recalculate the remaining credit
                const remaining = prepaymentTotal - (amountRefunded + creditAmount)
                return Math.abs(remaining);
            }
            default:
                return 0;
        }
    }

    /**
     * get the outstanding amount from the invoice for payment
     */
    public getPaymentAmount(
        config: { absolute: boolean } = { absolute: true }
    ): number {

        const { type, prepaymentTotal, outstandingAmount, amountPaid } = this.invoice;

        let value = 0

        switch (type) {
            case InvoiceType.CreditNote:
                // no payment for credit note
                break;
            case InvoiceType.PrepaymentInvoice:
                value = prepaymentTotal - amountPaid;
                break;
            default:
                value = outstandingAmount;
                break;
        }
        return config?.absolute ? Math.abs(value) : value;
    }


    /**
     * Recalculate all amount in every type of invoice (PP, CR, Invoice)
     * @param request any added amount to the invoice. And if reconcile stage is changed recalculated the rounding amount
     * @returns all amount infos
     */
    public calculateAmounts(
        request?: { addPaidAmount?: number, addCreditAmount?: number, addRefundedAmount?: number, newReconcileStage?: boolean }
    ): Pick<InvoiceCalculationModel, 'amountPaid' | 'amountRefunded' | 'creditAmount' | 'outstandingAmount' | 'roundingAmount'> {

        let { total, prepaymentTotal, amountPaid, creditAmount, amountRefunded, roundingAmount, type } = this.invoice;

        const newPaidAmount = amountPaid + (request?.addPaidAmount || 0);
        const newCreditAmount = creditAmount + (request?.addCreditAmount || 0);
        const newRefundedAmount = amountRefunded + (request?.addRefundedAmount || 0);
        let newRoundingAmount = roundingAmount;

        let outstandingAmount = 0;

        switch (type) {
            case InvoiceType.Invoice:
                const hasNewReconcileStage = typeof request?.newReconcileStage === 'boolean';


                if (!hasNewReconcileStage) {
                    outstandingAmount = total - (newPaidAmount + newCreditAmount + newRoundingAmount);
                    break;
                }

                const diffAmount = total - (newPaidAmount + newCreditAmount);

                if (request?.newReconcileStage) {
                    newRoundingAmount = diffAmount;
                    outstandingAmount = 0;
                } else {
                    newRoundingAmount = 0;
                    outstandingAmount = diffAmount;
                }

                break;
            case InvoiceType.CreditNote:
                // CR total is negative
                outstandingAmount = total + (newCreditAmount + newRefundedAmount);
                break;
            case InvoiceType.PrepaymentInvoice:
                // PP total is negative
                const isNew = newPaidAmount === 0;

                if (isNew || !this.isFullyPaid()) {
                    // hacky way to make sure the prepayment invoice is not paid yet
                    outstandingAmount = 0;
                    break;
                }

                outstandingAmount = -(prepaymentTotal - (newCreditAmount + newRefundedAmount));
                break;
        }

        return {
            amountPaid: new Decimal(newPaidAmount ?? 0).toDP(2).toNumber(),
            creditAmount: new Decimal(newCreditAmount ?? 0).toDP(2).toNumber(),
            amountRefunded: new Decimal(newRefundedAmount ?? 0).toDP(2).toNumber(),
            roundingAmount: new Decimal(newRoundingAmount ?? 0).toDP(2).toNumber(),
            outstandingAmount: new Decimal(outstandingAmount ?? 0).toDP(2).toNumber(),
        };
    }

}